import { useCallback } from 'react'

import { Box, styled } from '@mui/material'
import { PickersDayProps } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DateCalendar, dayCalendarClasses } from '@mui/x-date-pickers/DateCalendar'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { PickersDay } from '@mui/x-date-pickers/PickersDay'

import { useDateFnsLocale } from '../../../hooks/useDateFnsLocale'
import type {
  CalendarClickType,
  AnnualCalendarPublicProps as Props,
  SelectionAsRecord,
} from '../../typings'
import { parseDate } from '../../utils'

import { format } from 'date-fns'

import type { ValidColor } from '@/local/components'
import { Dot } from '../../Dot'

type CustomDayProps = PickersDayProps<Date> & {
  highlightedDays?: SelectionAsRecord
  dotColor: ValidColor
}

export function Calendar(props: Props) {
  const {
    selection,
    minDate,
    maxDate,
    highlightedDays,
    dotColor = '$pigmentGreen',
    onChange,
  } = props

  const minimumDate = minDate ? parseDate(minDate) : undefined
  const maximumDate = maxDate ? parseDate(maxDate) : undefined

  const locale = useDateFnsLocale()

  const handleOnChange = useCallback(
    (date: Date, type: CalendarClickType = 'click') => {
      onChange(date, selection, type)
    },
    [onChange, selection],
  )

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
      <CustomDateCalendar
        value={selection}
        onChange={value => handleOnChange(value)}
        views={['day']}
        slots={{
          day: CustomDay,
        }}
        slotProps={{
          calendarHeader: {
            format: 'MMMM',
          },
          day: ({ day }) =>
            ({
              highlightedDays,
              dotColor,
              'data-testid': format(day, 'MMdd'),
            }) as any,
        }}
        minDate={minimumDate}
        maxDate={maximumDate}
      />
    </LocalizationProvider>
  )
}

const CustomDateCalendar = styled(DateCalendar)(() => ({
  [`& .${dayCalendarClasses.header}`]: {
    display: 'none',
  },
  '&.MuiDateCalendar-root': {
    width: 'auto',
    height: 'auto',
    marginBottom: '32px',
  },
  '& .MuiPickersCalendarHeader-root': {
    padding: 0,
    marginTop: '24px',
  },
  '& .MuiPickersSlideTransition-root.MuiDayCalendar-slideTransition': {
    minHeight: '230px',
  },
}))

const CustomDay = (props: CustomDayProps) => {
  const { day, selected, highlightedDays, dotColor, ...other } = props

  const dayOfMonth = format(day, 'MMdd')
  const isHighlighted =
    highlightedDays && Object.keys(highlightedDays).some(date => date.slice(-4) === dayOfMonth)

  return (
    <Box position="relative" display="flex">
      <PickersDay {...other} day={day} selected={selected} />
      {isHighlighted && <Dot background={dotColor} />}
    </Box>
  )
}
